import { FormattedMessage, useIntl } from "react-intl";
import {
  Outlet,
  ShouldRevalidateFunction,
  useMatch,
  useNavigate,
} from "react-router";
import { DatasetAnalytics } from "@/components/DatasetAnalytics";
import { DatasetEmpty } from "@/components/DatasetEmpty";
import { DatasetVersionAsideContent } from "@/components/DatasetVersionAsideContent";
import { ArrowUpIcon } from "@/icons";
import { AsideRoot } from "@/kit/ui/aside";
import ErrorPage from "@/pages/_error";
import { Environment, graphql } from "react-relay";
import { safeParse } from "valibot";
import { preload, usePreloaded } from "@/utils/relay";
import { LoaderArgs } from "@/utils/route";
import { DatasetContentScaffoldQuery as DatasetContentScaffoldQueryType } from "./__generated__/DatasetContentScaffoldQuery.graphql";
import { DatasetContentScaffoldVersionQuery as DatasetContentScaffoldVersionQueryType } from "./__generated__/DatasetContentScaffoldVersionQuery.graphql";
import { DatasetContentScaffoldParams } from "@/utils/dataset";
import { isEqual } from "lodash";
export default function DatasetContentScaffold() {
  const intl = useIntl();
  const {
    query: { datasetBySlug: dataset },
  } = useDatasetContentScaffoldQuery();
  const version = dataset
    ? "version" in dataset
      ? dataset.version
      : dataset.versions.nodes[0]
    : null;
  const isEditSheetOpen =
    useMatch("/datasets/:owner/:slug/versions/:version/edit") !== null;
  const navigate = useNavigate();
  if (!dataset) {
    return (
      <ErrorPage
        status={404}
        message={intl.formatMessage({ defaultMessage: "Dataset not found" })}
      />
    );
  }
  if (!version) {
    return <DatasetEmpty datasetId={dataset.id} />;
  }
  return (
    <AsideRoot>
      <DatasetVersionAsideContent
        datasetVersion={version}
        editSheetOpen={isEditSheetOpen}
        onEditSheetOpenChange={(open) => {
          if (!open) navigate(-1);
        }}
        analytics={
          <DatasetAnalytics analytics={version.analytics}>
            <div className="flex flex-row items-center gap-2">
              <ArrowUpIcon className="w-5 h-5 text-grey-500" />
              <p className="text-sm font-normal leading-5 text-grey-600">
                <FormattedMessage
                  defaultMessage="{voteCount, plural, =1 {# Upvote} other {# Upvotes}}"
                  values={{ voteCount: dataset.votes }}
                />
              </p>
            </div>
          </DatasetAnalytics>
        }
      />

      <Outlet />
    </AsideRoot>
  );
}
const DatasetContentScaffoldQuery = graphql`
  query DatasetContentScaffoldQuery($owner: String!, $slug: String!) {
    datasetBySlug(owner: $owner, localSlug: $slug) {
      id
      votes
      versions(first: 1) {
        nodes {
          id
          ...DatasetVersionAsideContentFragment
          analytics {
            ...DatasetAnalyticsFragment
          }
        }
      }
    }
  }
`;
const DatasetContentScaffoldVersionQuery = graphql`
  query DatasetContentScaffoldVersionQuery(
    $owner: String!
    $slug: String!
    $major: Int!
    $minor: Int!
    $patch: Int!
  ) {
    datasetBySlug(owner: $owner, localSlug: $slug) {
      id
      votes
      version(major: $major, minor: $minor, patch: $patch) {
        id
        ...DatasetVersionAsideContentFragment
        analytics {
          ...DatasetAnalyticsFragment
        }
      }
    }
  }
`;
export const loader =
  (environment: Environment) => (loaderArgs: LoaderArgs) => {
    const parse = safeParse(DatasetContentScaffoldParams, loaderArgs.params);
    if (!parse.success) {
      throw new Response("Invalid request params", { status: 400 });
    }
    if ("version" in parse.output) {
      const {
        version: [major, minor, patch],
        ...params
      } = parse.output;
      return preload<DatasetContentScaffoldVersionQueryType>(
        environment,
        DatasetContentScaffoldVersionQuery,
        {
          ...params,
          major,
          minor,
          patch,
        },
      );
    } else {
      return preload<DatasetContentScaffoldQueryType>(
        environment,
        DatasetContentScaffoldQuery,
        parse.output,
      );
    }
  };
const useDatasetContentScaffoldQuery = () =>
  usePreloaded<
    DatasetContentScaffoldQueryType | DatasetContentScaffoldVersionQueryType
  >();

export const shouldRevalidate: ShouldRevalidateFunction = ({
  currentParams,
  nextParams,
}) => !isEqual(currentParams, nextParams);
